// src/components/appointments/WeekCalendar.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "react-toastify";

import { useAuth } from "../../context/AuthContext";
import { usePatients } from "../../context/PatientsContext";
import { useAppointments } from "../../context/AppointmentsContext";
import { useWorkingHours } from "../../context/WorkingHoursContext";
import { useUsers } from "../../context/UsersContext";
import { useUiPreferences } from "../../context/UiPreferencesContext";
import { useServices } from "../../context/ServicesContext";

import {
  saveRadiograph,
  downloadRadiograph,
  getRadiographUrl,
  loadRadiograph,
} from "../../services/radiographyService";

import { addDays, getMonday, formatDate, formatDateLocal } from "../../utils/dateUtils";
import { isValidPhone, isValidCNP } from "../../utils/validation";
import {
  WORKING_DAY_ITEMS,
  buildHourSlots,
  computeHourWindow,
  describeDaySchedule,
  isSlotWithinWorkingHours,
} from "../../utils/workingHours";

// UI
import CalendarNavigation from "./CalendarNavigation";
import CalendarGrid from "./CalendarGrid";
import AppointmentFormModal from "./AppointmentFormModal";
import RadiographyPreviewModal from "./RadiographyPreviewModal";
import { formatNamePart, sanitizePhone, sanitizeCnp } from "../../utils/textUtils";

// Helpers
const DEFAULT_DAY_ITEMS = WORKING_DAY_ITEMS.slice(0, 5);
const labelToOffset = WORKING_DAY_ITEMS.reduce((acc, item, index) => {
  acc[item.label] = index;
  return acc;
}, {});
const normalizeEmailValue = (value = "") => value.trim().toLowerCase();
const createEmptyFormData = () => ({
  lastName: "",
  firstName: "",
  phone: "",
  email: "",
  cnp: "",
  city: "",
  notes: "",
  isMinor: false,
  legalRepresentative: {
    lastName: "",
    firstName: "",
    city: "",
    relation: "",
  },
  image: null,
  radiographs: [],
});

const toFormDataFromPatient = (patient, overrides = {}) => ({
  lastName: formatNamePart(patient?.lastName || ""),
  firstName: formatNamePart(patient?.firstName || ""),
  phone: sanitizePhone(patient?.phone || ""),
  email: typeof patient?.email === "string" ? patient.email : "",
  cnp: sanitizeCnp(patient?.cnp || ""),
  city: typeof patient?.city === "string" ? patient.city : "",
  notes: overrides.notes ?? "",
  isMinor: !!patient?.isMinor,
  legalRepresentative: {
    lastName: patient?.legalRepresentative?.lastName || "",
    firstName: patient?.legalRepresentative?.firstName || "",
    city: patient?.legalRepresentative?.city || "",
    relation: patient?.legalRepresentative?.relation || "",
  },
  image: null,
  radiographs: [],
});

export default function WeekCalendar({ onAppointmentPatientSelect, preselectedPatient = null }) {
  const { currentUser } = useAuth();
  const { pacienti, createPatient } = usePatients();
  const {
    appointments,
    upsertAppointmentGroup,
    cancelAppointmentGroup,
    rescheduleAppointmentGroup,
    moveAppointmentGroup,
    resizeAppointmentGroup,
    refreshAppointments,
  } = useAppointments();
  const { workingHours } = useWorkingHours();
  const { users } = useUsers();
  const { userColors, categoryColors } = useUiPreferences();
  const { services } = useServices();

  const [weekStart, setWeekStart] = useState(getMonday(new Date()));

  const activeDayItems = useMemo(() => {
    const enabled = WORKING_DAY_ITEMS.filter(({ key }) => {
      const cfg = workingHours?.[key];
      return cfg?.enabled && Array.isArray(cfg.intervals) && cfg.intervals.length > 0;
    });
    return enabled.length ? enabled : DEFAULT_DAY_ITEMS;
  }, [workingHours]);

  const visibleDays = useMemo(() => activeDayItems.map((item) => item.label), [activeDayItems]);

  const weekDates = useMemo(
    () =>
      activeDayItems.map((item) =>
        addDays(weekStart, labelToOffset[item.label] ?? 0)
      ),
    [activeDayItems, weekStart]
  );

  const [selectedSlot, setSelectedSlot] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [actionNote, setActionNote] = useState("");
  const [pendingReschedule, setPendingReschedule] = useState(null);
  const RESCHEDULE_MIN_HOURS = 0.5;
  const RESCHEDULE_MINUTES = 30;

  const [selectedPatientId, setSelectedPatientId] = useState("");

  const [formData, setFormData] = useState(() => createEmptyFormData());
  const [selectedCategory, setSelectedCategory] = useState("");

  const [recall, setRecall] = useState("none");
  const [duration, setDuration] = useState(0.5);
  const [dragInfo, setDragInfo] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);

  const hourWindow = useMemo(
    () =>
      computeHourWindow({
        workingHours,
        appointments,
      }),
    [workingHours, appointments]
  );

  const hours = useMemo(() => buildHourSlots(hourWindow), [hourWindow]);

  const daySchedules = useMemo(
    () =>
      visibleDays.reduce((acc, label) => {
        acc[label] = describeDaySchedule(workingHours, label);
        return acc;
      }, {}),
    [workingHours, visibleDays]
  );

  const appointmentCategoryOptions = useMemo(() => {
    const categoryMap = new Map();
    (services || []).forEach((service) => {
      const name = (service?.category || "").trim();
      if (!name) return;
      const order = Number(service?.categoryOrder || 0);
      if (!categoryMap.has(name)) {
        categoryMap.set(name, order);
      } else {
        categoryMap.set(name, Math.min(categoryMap.get(name), order));
      }
    });
    return Array.from(categoryMap.entries())
      .sort((a, b) => (a[1] - b[1]) || a[0].localeCompare(b[0]))
      .map(([name]) => name);
  }, [services]);

  const currentRole = currentUser?.role || null;
  const isAdmin = currentRole === "admin";
  const canManageAppointments = ["admin", "doctor", "assistant"].includes(currentRole || "");
  const currentEmail = currentUser?.email?.trim().toLowerCase() || "";

  const userColorByEmail = useMemo(() => {
    if (!isAdmin) return {};
    const map = {};
    (users || []).forEach((user) => {
      const email = (user.email || "").trim().toLowerCase();
      const color = userColors?.[user.id];
      if (email && color) {
        map[email] = color;
      }
    });
    return map;
  }, [isAdmin, users, userColors]);

  const isDoctorAssignedToAppointment = useCallback(
    (appointment) => {
      if (!appointment || !currentEmail) return false;
      const doctorEmail = (appointment.doctorEmail || "")
        .toString()
        .trim()
        .toLowerCase();
      return doctorEmail === currentEmail;
    },
    [currentEmail]
  );

  const isAppointmentOwner = useCallback(
    (appointment) => {
      if (!appointment) return false;
      const creator =
        (appointment.createdBy || appointment.createdByEmail || "")
          .toString()
          .trim()
          .toLowerCase();
      if (currentEmail && creator === currentEmail) {
        return true;
      }
      if (currentRole === "doctor" && isDoctorAssignedToAppointment(appointment)) {
        return true;
      }
      return false;
    },
    [currentEmail, currentRole, isDoctorAssignedToAppointment]
  );

  const canEditAppointment = useCallback(
    (appointment) => {
      if (!appointment) return false;
      if (isAdmin) return true;
      if (isAppointmentOwner(appointment)) return true;
      if (currentRole === "assistant") return false;
      return currentRole === "doctor" && isDoctorAssignedToAppointment(appointment);
    },
    [isAdmin, currentRole, isAppointmentOwner, isDoctorAssignedToAppointment]
  );

  // sync formular când alegi pacient existent
  useEffect(() => {
    if (!selectedPatientId) return;
    const existing = pacienti.find((p) => p.id === selectedPatientId);
    if (existing) {
      setFormData((prev) => toFormDataFromPatient(existing, { notes: prev.notes }));
    }
  }, [selectedPatientId, pacienti]);

  // la editare → preia recall/durată
  useEffect(() => {
    if (!currentUser) return;
    if (!selectedSlot?.appointmentId && !selectedSlot?.rescheduleFromId) {
      setRecall("none");
      setDuration(0.5);
      setActionNote("");
      setSelectedCategory("");
      return;
    }
    const existingApp = appointments.find(
      (a) => a.id === selectedSlot.appointmentId
    );
    if (existingApp) {
      setRecall(existingApp.recall || "none");
      setDuration(existingApp.duration || 0.5);
    }
  }, [selectedSlot, appointments, currentUser]);

  // click slot (creare/editare)
  const handleSlotClick = async (day, hour, appointmentId = null) => {
    if (!currentUser) return;

    const offset = labelToOffset[day];
    if (typeof offset !== "number") return;
    const dateObj = addDays(weekStart, offset);
    const dateStr = formatDateLocal(dateObj);

    if (appointmentId) {
      if (pendingReschedule) {
        toast.info("Alege un slot liber pentru reprogramare.");
        return;
      }
      const appObj = appointments.find((a) => a.id === appointmentId);
      if (!appObj) return;

      const canAccess = isAdmin || canEditAppointment(appObj);
      if (!canAccess) return;

      setPendingReschedule(null);
      const existingPatient = pacienti.find((p) => p.id === appObj.patientId);
      const fallbackPatient = {
        id: appObj.patientId,
        code: appObj.patientCode,
        lastName: appObj.lastName,
        firstName: appObj.firstName,
        phone: appObj.phone,
        cnp: appObj.cnp,
        city: appObj.city || "",
        medicEmail: appObj.doctorEmail || appObj.medicEmail || "",
        isMinor: appObj.isMinor || false,
        legalRepresentative: appObj.legalRepresentative || {
          lastName: "",
          firstName: "",
          city: "",
          relation: "",
        },
      };

      const patientObj = existingPatient || fallbackPatient;

      const mainId = appObj.mainAppointmentId || appObj.id;

      setSelectedSlot({
        day,
        hour,
        date: dateStr,
        appointmentId: appObj.id,
        mainAppointmentId: mainId,
      });
      setFormData(
        toFormDataFromPatient(patientObj, { notes: appObj.notes || "" })
      );
      setSelectedPatientId(patientObj.id || "");
      setActionNote("");
      setSelectedCategory(appObj.category || "");

      if (isAdmin && typeof onAppointmentPatientSelect === "function") {
        onAppointmentPatientSelect(existingPatient || fallbackPatient);
      }

      (async () => {
        try {
          const existingImage = await loadRadiograph(mainId);
          if (existingImage) {
            setFormData((prev) => ({
              ...prev,
              image: existingImage,
              radiographs: [{ id: mainId, url: existingImage }],
            }));
          }
        } catch (error) {
          console.warn("Nu am putut încărca radiografia existentă.", error);
        }
      })();

      return;
    }

    if (!canManageAppointments) return;

    const desiredDuration = pendingReschedule ? RESCHEDULE_MIN_HOURS : (duration || 0.5);
    if (!isSlotWithinWorkingHours(workingHours, day, hour, desiredDuration)) {
      toast.error("Acest interval nu este disponibil în programul de lucru.");
      return;
    }

    if (pendingReschedule) {
      const rescheduleApp = pendingReschedule.appointment;
      const durationHours = RESCHEDULE_MIN_HOURS;
      const available = isRangeAvailable(
        day,
        dateStr,
        hour,
        durationHours,
        pendingReschedule.mainId
      );
      if (!available) {
        toast.error("Slot-ul este deja ocupat.");
        return;
      }

      try {
        await rescheduleAppointmentGroup(pendingReschedule.mainId, {
          date: dateStr,
          startTime: `${String(Math.floor(hour)).padStart(2, "0")}:${hour % 1 === 0 ? "00" : "30"}`,
          durationMinutes: RESCHEDULE_MINUTES,
          notes: rescheduleApp.notes || "",
          recall: rescheduleApp.recall || "none",
          hasRadiograph: !!rescheduleApp.hasRadiograph,
          category: rescheduleApp.category || "",
          note: pendingReschedule.note || "",
        });
        setPendingReschedule(null);
        setActionNote("");
        setSelectedCategory("");
        toast.success("Programare reprogramată!");
      } catch (err) {
        console.error("Eroare reprogramare:", err);
        toast.error(err.message || "Nu s-a putut reprograma programarea");
      }
      return;
    }

    setSelectedSlot({ day, hour, date: dateStr, appointmentId: null, mainAppointmentId: null });
    if (preselectedPatient?.id) {
      setSelectedPatientId(preselectedPatient.id);
      setFormData(toFormDataFromPatient(preselectedPatient));
    } else {
      setSelectedPatientId("");
      setFormData(createEmptyFormData());
    }
    setActionNote("");
    setSelectedCategory("");
  };

  const isRangeAvailable = useCallback(
    (day, date, startHour, rangeDuration, ignoreMainId = null) => {
      if (!isSlotWithinWorkingHours(workingHours, day, startHour, rangeDuration)) {
        return false;
      }
      const endHour = startHour + rangeDuration - 0.001;
      return !(appointments || []).some((app) => {
        if (app.date !== date || app.day !== day) return false;
        if (ignoreMainId && app.mainAppointmentId === ignoreMainId) return false;
        const slotStart = app.hour;
        const slotEnd = slotStart + 0.5 - 0.001;
        return slotStart <= endHour && slotEnd >= startHour;
      });
    },
    [appointments, workingHours]
  );

  const handleDragStart = useCallback(
    (event, app) => {
      const canEdit = isAdmin || canEditAppointment(app);
      if (!canEdit) {
        event.preventDefault();
        return;
      }

      const mainId = app.mainAppointmentId || app.id;
      const groupSlots = (appointments || []).filter(
        (slot) => slot.mainAppointmentId === mainId
      );
      if (groupSlots.length === 0) {
        event.preventDefault();
        return;
      }

      const primarySlot = groupSlots.reduce((earliest, current) =>
        current.hour < earliest.hour ? current : earliest
      );

      const offset = app.hour - primarySlot.hour;
      const groupDuration = groupSlots.length * 0.5;

      setDragInfo({
        mode: "move",
        mainId,
        offset,
        duration: groupDuration,
        startHour: primarySlot.hour,
        day: primarySlot.day,
        date: primarySlot.date,
      });

      try {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData(
          "application/json",
          JSON.stringify({ mainId })
        );
      } catch {
        /* ignore */
      }
    },
    [appointments, isAdmin, canEditAppointment]
  );

  const handleDragEnd = useCallback(() => {
    setDragInfo(null);
    setDragOverSlot(null);
  }, []);

  const handleResizeStart = useCallback(
    (event, app) => {
      const canEdit = isAdmin || canEditAppointment(app);
      if (!canEdit) {
        event.preventDefault();
        return;
      }

      const mainId = app.mainAppointmentId || app.id;
      const groupSlots = (appointments || []).filter(
        (slot) => slot.mainAppointmentId === mainId
      );
      if (groupSlots.length === 0) {
        event.preventDefault();
        return;
      }

      const primarySlot = groupSlots.reduce((earliest, current) =>
        current.hour < earliest.hour ? current : earliest
      );

      const groupDuration = groupSlots.length * 0.5;

      setDragInfo({
        mode: "resize",
        mainId,
        offset: 0,
        duration: groupDuration,
        startHour: primarySlot.hour,
        day: primarySlot.day,
        date: primarySlot.date,
      });

      try {
        event.stopPropagation();
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData(
          "application/json",
          JSON.stringify({ mainId, action: "resize" })
        );
      } catch {
        /* ignore */
      }
    },
    [appointments, isAdmin, canEditAppointment]
  );

  const handleDragOver = useCallback(
    (event, day, date, hour, occupyingMainId = null) => {
      if (!dragInfo) return;
      if (occupyingMainId && occupyingMainId !== dragInfo.mainId) return;
      if (dragInfo.mode === "resize" && day !== dragInfo.day) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setDragOverSlot({ day, date, hour });
    },
    [dragInfo]
  );

  const handleDrop = useCallback(
    async (event, day, date, hour, occupyingMainId = null) => {
      event.preventDefault();
      setDragOverSlot(null);
      if (!dragInfo) return;
      if (occupyingMainId && occupyingMainId !== dragInfo.mainId) return;

      const dateStr = date;

      if (dragInfo.mode === "move") {
        let targetStart = hour - dragInfo.offset;
        const minHour = 9;
        const maxHour = 21 - dragInfo.duration;
        if (targetStart < minHour) targetStart = minHour;
        if (targetStart > maxHour) targetStart = maxHour;
        targetStart = Math.round(targetStart * 2) / 2;

        const available = isRangeAvailable(
          day,
          dateStr,
          targetStart,
          dragInfo.duration,
          dragInfo.mainId
        );

        if (!available) {
          toast.error("Slot-ul este deja ocupat.");
          return;
        }

        try {
          await moveAppointmentGroup(dragInfo.mainId, {
            day,
            date: dateStr,
            hour: targetStart,
          });
        } catch (err) {
          console.error("Eroare mutare programare:", err);
          toast.error(err.message || "Nu s-a putut muta programarea");
        }
      } else if (dragInfo.mode === "resize") {
        if (day !== dragInfo.day || dateStr !== dragInfo.date) return;

        let newDuration = hour - dragInfo.startHour + 0.5;
        if (newDuration < 0.5) newDuration = 0.5;
        const maxDuration = 21 - dragInfo.startHour;
        if (newDuration > maxDuration) newDuration = maxDuration;
        newDuration = Math.round(newDuration * 2) / 2;

        const available = isRangeAvailable(
          day,
          dateStr,
          dragInfo.startHour,
          newDuration,
          dragInfo.mainId
        );

        if (!available) {
          toast.error("Extinderea depășește un slot ocupat.");
          return;
        }

        try {
          await resizeAppointmentGroup(dragInfo.mainId, newDuration);
        } catch (err) {
          console.error("Eroare redimensionare programare:", err);
          toast.error(err.message || "Nu s-a putut redimensiona programarea");
        }
      }
    },
    [dragInfo, isRangeAvailable, moveAppointmentGroup, resizeAppointmentGroup]
  );

  // SAVE programare + pacient + Rx
  const handleSave = async ({ resolvedPatientId } = {}) => {
    if (!selectedSlot) return;
    if (!currentUser) {
      toast.error("Nu ești autentificat.");
      return;
    }
    if (pendingReschedule && !selectedSlot?.rescheduleFromId) {
      toast.info("Selectează un slot liber pentru reprogramare.");
      return;
    }
    if (!selectedSlot?.date || typeof selectedSlot.hour !== "number") {
      toast.error("Selectează un slot valid înainte de salvare.");
      return;
    }

    let patientId,
      finalLastName,
      finalFirstName,
      finalPhone,
      finalEmail = "",
      finalCnp,
      finalCity = "",
      finalIsMinor = false,
      finalRepresentative = { lastName: "", firstName: "", city: "", relation: "" },
      finalCode = "";
    const base64Image = typeof formData.image === "string" ? formData.image : null;
    let shouldAddPatient = false;

    const effectiveSelectedPatientId = resolvedPatientId || selectedPatientId;

    if (effectiveSelectedPatientId) {
      const existing = pacienti.find((p) => p.id === effectiveSelectedPatientId);
      if (!existing) {
        toast.error("Pacientul selectat nu există!");
        return;
      }
      patientId = existing.id;
      finalLastName = existing.lastName;
      finalFirstName = existing.firstName;
      finalPhone = existing.phone;
      finalEmail = existing.email || "";
      finalCnp = existing.cnp || "";
      finalCode = existing.code || "";
      finalCity = typeof existing.city === "string" ? existing.city : "";
      finalIsMinor = !!existing.isMinor;
      finalRepresentative = {
        lastName: existing.legalRepresentative?.lastName || "",
        firstName: existing.legalRepresentative?.firstName || "",
        city: existing.legalRepresentative?.city || "",
        relation: existing.legalRepresentative?.relation || "",
      };
    } else {
      const rawLastName = formData.lastName?.trim();
      const rawFirstName = formData.firstName?.trim();
      const sanitizedPhoneInput = sanitizePhone(formData.phone);
      const rawEmailInput = (formData.email || "").trim();
      const sanitizedCnpInput = sanitizeCnp(formData.cnp);
      const rawCity = formData.city?.trim() || "";
      const isMinor = !!formData.isMinor;
      const representativeInput = {
        lastName: formData.legalRepresentative?.lastName?.trim() || "",
        firstName: formData.legalRepresentative?.firstName?.trim() || "",
        city: formData.legalRepresentative?.city?.trim() || "",
        relation: formData.legalRepresentative?.relation?.trim() || "",
      };

      if (!rawLastName || !rawFirstName || !sanitizedPhoneInput) {
  toast.error("Completează nume, prenume și telefon!");
  return;
}

if (!isValidPhone(sanitizedPhoneInput)) {
  toast.error("Numărul de telefon trebuie să conțină doar cifre.");
  return;
}

if (sanitizedCnpInput && !isValidCNP(sanitizedCnpInput)) {
  toast.error("CNP invalid (13 cifre).");
  return;
}

      const trimmedEmail = normalizeEmailValue(rawEmailInput);
      if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        toast.error("Introdu o adresă de email validă sau lasă câmpul gol.");
        return;
      }

      if (isMinor) {
        if (!representativeInput.lastName || !representativeInput.firstName || !representativeInput.relation) {
          toast.error("Completează nume, prenume și calitatea reprezentantului legal.");
          return;
        }
      }

      const existing = pacienti.find((p) => {
        const sameName =
          formatNamePart(p.lastName) === formatNamePart(rawLastName) &&
          formatNamePart(p.firstName) === formatNamePart(rawFirstName);
        const sameCnp = sanitizeCnp(p.cnp) === sanitizedCnpInput;
        return sameName && sameCnp;
      });

      if (existing) {
        patientId = existing.id;
        finalLastName = existing.lastName;
        finalFirstName = existing.firstName;
        finalPhone = existing.phone;
        finalEmail = existing.email || "";
        finalCnp = existing.cnp;
        finalCode = existing.code || "";
        finalCity = typeof existing.city === "string" ? existing.city : "";
        finalIsMinor = !!existing.isMinor;
        finalRepresentative = {
          lastName: existing.legalRepresentative?.lastName || "",
          firstName: existing.legalRepresentative?.firstName || "",
          city: existing.legalRepresentative?.city || "",
          relation: existing.legalRepresentative?.relation || "",
        };
      } else {
        patientId = `p-${Date.now()}`;
        finalLastName = rawLastName;
        finalFirstName = rawFirstName;
        finalPhone = sanitizedPhoneInput;
        finalEmail = trimmedEmail;
        finalCnp = sanitizedCnpInput;
        finalCity = rawCity;
        finalIsMinor = isMinor;
        finalRepresentative = representativeInput;
        shouldAddPatient = true;
      }
    }

    const formattedLastName = formatNamePart(finalLastName);
    const formattedFirstName = formatNamePart(finalFirstName);
    const sanitizedPhone = sanitizePhone(finalPhone);
    const sanitizedCnp = sanitizeCnp(finalCnp);

    if (!formattedLastName || !formattedFirstName) {
      toast.error("Completează nume și prenume valide!");
      return;
    }

    const formattedCity = formatNamePart(finalCity || "");
    const normalizedEmail = normalizeEmailValue(finalEmail);
    const normalizedRepresentative = {
      lastName: formatNamePart(finalRepresentative.lastName || ""),
      firstName: formatNamePart(finalRepresentative.firstName || ""),
      city: formatNamePart(finalRepresentative.city || ""),
      relation: finalRepresentative.relation?.trim() || "",
    };

    if (shouldAddPatient) {
      try {
        const created = await createPatient({
          id: patientId,
          lastName: formattedLastName,
          firstName: formattedFirstName,
          phone: sanitizedPhone,
          email: normalizedEmail,
          cnp: sanitizedCnp,
          city: formattedCity,
          isMinor: finalIsMinor,
          legalRepresentative: normalizedRepresentative,
          notes: formData.notes || "",
          medicEmail: currentUser?.email || "",
          radiographs: [],
        });
        finalCode = created?.code || finalCode;
        if (created?.id) {
          patientId = created.id;
        }
      } catch (err) {
        console.error("Eroare creare pacient:", err);
        toast.error(err.message || "Nu s-a putut salva pacientul");
        return;
      }
    }
    if (!patientId) {
      toast.error("Selectează sau creează un pacient înainte de programare.");
      return;
    }
    if (!finalCode) {
      const matched = pacienti.find((p) => p.id === patientId);
      finalCode = matched?.code || finalCode;
    }

    // 2) Salvează grupul de programări în context
    let mainId;
    try {
      if (selectedSlot?.rescheduleFromId) {
        mainId = await rescheduleAppointmentGroup(selectedSlot.rescheduleFromId, {
          date: selectedSlot.date,
          startTime: `${String(Math.floor(selectedSlot.hour)).padStart(2, "0")}:${selectedSlot.hour % 1 === 0 ? "00" : "30"}`,
          durationMinutes: RESCHEDULE_MINUTES,
          notes: formData.notes,
          recall,
          hasRadiograph: !!base64Image,
          category: selectedCategory || "",
          note: actionNote || "",
        });
      } else {
        mainId = await upsertAppointmentGroup({
          existingAppointmentId: selectedSlot?.appointmentId || null,
          day: selectedSlot.day,
          date: selectedSlot.date,
          hour: selectedSlot.hour,
          duration,
          createdBy: currentUser?.email || "sistem",
          patient: {
            id: patientId,
            code: finalCode,
            lastName: formattedLastName,
            firstName: formattedFirstName,
            phone: sanitizedPhone,
            email: normalizedEmail,
            cnp: sanitizedCnp,
            city: formattedCity,
            isMinor: finalIsMinor,
            legalRepresentative: normalizedRepresentative,
          },
          notes: formData.notes,
          recall,
          hasRadiograph: !!base64Image,
          category: selectedCategory || "",
        });
      }
    } catch (err) {
      console.error("Eroare salvare programare:", err);
      toast.error(err.message || "Nu s-a putut salva programarea");
      return;
    }

    // 3) Salvează Rx în storage
    if (base64Image) {
      try {
        await saveRadiograph(mainId, base64Image);
        refreshAppointments?.();
      } catch (e) {
        console.error(e);
        toast.error("Nu am putut salva radiografia.");
        return;
      }
    }

    // cleanup
    setSelectedSlot(null);
    setFormData(createEmptyFormData());
    setSelectedPatientId("");
    setPendingReschedule(null);
    setActionNote("");
    setSelectedCategory("");
    toast.success("Programare salvată cu succes!");
  };

  const handleCancelAppointment = async (slot, note = "") => {
    const confirmed = window.confirm("Anulezi această programare?");
    if (!confirmed) return;

    const baseId = slot.mainAppointmentId || slot.appointmentId;
    if (!baseId) return;
    const mainId = baseId.includes("-p") ? baseId.split("-p")[0] : baseId;

    try {
      await cancelAppointmentGroup(mainId, note);
    } catch (err) {
      console.error("Eroare anulare programare:", err);
      toast.error(err.message || "Nu s-a putut anula programarea");
      return;
    }

    setSelectedSlot(null);
    setFormData(createEmptyFormData());
    setPreviewImage(null);
    setActionNote("");
    setSelectedCategory("");
    toast.success("Programare anulată!");
  };

  const handleStartReschedule = (slot, note = "") => {
    const baseId = slot.mainAppointmentId || slot.appointmentId;
    if (!baseId) return;
    const mainId = baseId.includes("-p") ? baseId.split("-p")[0] : baseId;
    const groupSlots = (appointments || []).filter(
      (entry) => entry.mainAppointmentId === mainId
    );
    const primarySlot = groupSlots.length
      ? groupSlots.reduce((earliest, current) =>
          current.hour < earliest.hour ? current : earliest
        )
      : slot;
    setPendingReschedule({
      mainId,
      appointment: primarySlot,
      note,
    });
    setSelectedSlot(null);
    setFormData(createEmptyFormData());
    setSelectedPatientId("");
    setActionNote("");
    setSelectedCategory("");
    toast.info("Selectează un nou slot pentru reprogramare.");
  };

  // DESCHIDE Rx în modal
  const handleOpenRx = async (appointmentId) => {
    if (!currentUser) return;

    const resolveApp = (id) =>
      appointments.find((a) => a.id === id) ||
      appointments.find((a) => a.mainAppointmentId === id);

    const targetApp = resolveApp(appointmentId);
    if (!targetApp) {
      toast.error("Programarea nu a fost găsită.");
      return;
    }

    const isAdmin = currentUser.role === "admin";
    const userEmail = currentUser.email || "";
    const normalizedUserEmail = userEmail.trim().toLowerCase();
    const creatorEmail = (targetApp.createdBy || "")
      .toString()
      .trim()
      .toLowerCase();
    const doctorEmail = (targetApp.doctorEmail || "")
      .toString()
      .trim()
      .toLowerCase();

    const canAccess =
      isAdmin ||
      (normalizedUserEmail &&
        (creatorEmail === normalizedUserEmail || doctorEmail === normalizedUserEmail));

    if (!canAccess) {
      toast.error("Nu ai acces la această radiografie.");
      return;
    }

    const mainId = appointmentId.includes("-p")
      ? appointmentId.split("-p")[0]
      : appointmentId;

    try {
      const url = await getRadiographUrl(mainId);
      if (!url) {
        toast.error("Radiografia nu a fost găsită!");
        return;
      }
      setPreviewImage({ url, appointmentId: mainId, source: "appointment" });
    } catch (e) {
      console.error(e);
      toast.error("Eroare la încărcarea radiografiei!");
    }
  };

  return (
    <div className="bg-white p-3 sm:p-4 rounded-xl shadow-md">
      {/* Navigare săptămână */}
      <CalendarNavigation
        weekStart={weekStart}
        setWeekStart={setWeekStart}
        weekDates={weekDates}
        getMonday={getMonday}
        addDays={addDays}
        formatDate={formatDate}
      />

      {/* Calendar grid */}
      <CalendarGrid
        days={visibleDays}
        weekDates={weekDates}
        hours={hours}
        appointments={appointments}
        currentUser={currentUser}
        workingHours={workingHours}
        daySchedules={daySchedules}
        userColorByEmail={userColorByEmail}
        categoryColors={categoryColors}
        onSlotClick={handleSlotClick}
        onOpenRx={handleOpenRx}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOverSlot={handleDragOver}
        onDropSlot={handleDrop}
        onDragLeaveSlot={() => setDragOverSlot(null)}
        dragInfo={dragInfo}
        dragOverSlot={dragOverSlot}
        onResizeStart={handleResizeStart}
      />

      {/* Formular programare */}
      <AppointmentFormModal
        selectedSlot={selectedSlot}
        formData={formData}
        setFormData={setFormData}
        selectedPatientId={selectedPatientId}
        setSelectedPatientId={setSelectedPatientId}
        recall={recall}
        setRecall={setRecall}
        duration={duration}
        setDuration={setDuration}
        pacienti={pacienti}
        currentUser={currentUser}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        categoryOptions={appointmentCategoryOptions}
        categoryColors={categoryColors}
        onSave={handleSave}
        onCancelAppointment={handleCancelAppointment}
        onRescheduleAppointment={handleStartReschedule}
        actionNote={actionNote}
        setActionNote={setActionNote}
        lockPatient={Boolean(selectedSlot?.rescheduleFromId)}
        onCancel={() => {
          if (selectedSlot?.rescheduleFromId) {
            setPendingReschedule(null);
            setActionNote("");
          }
          setSelectedCategory("");
          setSelectedSlot(null);
        }}
      />

      {/* Modal radiografie */}
      <RadiographyPreviewModal
        previewImage={previewImage}
        onClose={() => setPreviewImage(null)}
        onDownload={(info) => info?.appointmentId && downloadRadiograph(info.appointmentId)}
      />
    </div>
  );
}
